import React, { useId, useState } from "react";
import { SERVICES } from "./services";

const ENQUIRY_ENDPOINT = "https://api.neoncrowgames.com/api/enquiries";

/** How far along the game is. Shapes the studio's first reply more than anything else does. */
const STAGES = [
  { value: "idea", label: "An idea, nothing built yet" },
  { value: "prototype", label: "A prototype we play ourselves" },
  { value: "playtested", label: "Playtested outside our group" },
  { value: "published", label: "Published or printed" },
];

interface Fields {
  name: string;
  email: string;
  organisation: string;
  service: string;
  stage: string;
  message: string;
  /** Honeypot — hidden from people, irresistible to scripts. */
  website: string;
}

const FALLBACK_ERROR =
  "That did not go through. Email neoncrowgames@gmail.com and we will pick it up there.";

/**
 * The server's message, but only if it is fit to show someone.
 *
 * Spring's *unhandled* error bodies carry the failing controller method's full
 * signature. The backend has a handler that replaces those with a sentence, but
 * a future endpoint, a proxy error page, or a gateway timeout can still put raw
 * internals in `message` — so anything that smells like a stack trace or a Java
 * package name is dropped in favour of the generic line rather than printed at
 * a prospective client.
 */
function readableMessage(body: unknown): string | null {
  if (!body || typeof body !== "object" || !("message" in body)) return null;
  const message = (body as { message: unknown }).message;
  if (typeof message !== "string") return null;

  const text = message.trim();
  if (!text) return null;
  if (text.length > 300) return null;
  if (/\b(org|com|java|jakarta)\.[a-z]/.test(text)) return null;
  if (/Exception|Validation failed for argument/i.test(text)) return null;

  return text;
}

const EMPTY: Fields = {
  name: "",
  email: "",
  organisation: "",
  service: SERVICES[0].slug,
  stage: "",
  message: "",
  website: "",
};

/**
 * The studio-services enquiry form.
 *
 * <p>Replaces a `mailto:` link, which silently loses everyone browsing without
 * a mail client configured — on a phone, most people. Posts to the backend,
 * which stores the enquiry and emails the studio.
 *
 * The `service` dropdown is built from the same `SERVICES` list the page
 * renders, so the value stored is always a slug the site still advertises.
 */
export default function EnquiryForm({ defaultService }: { defaultService?: string }) {
  const id = useId();
  const [fields, setFields] = useState<Fields>({
    ...EMPTY,
    service: defaultService ?? EMPTY.service,
  });
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [message, setMessage] = useState("");

  const set = (key: keyof Fields) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => setFields((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    setMessage("");

    try {
      const response = await fetch(ENQUIRY_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fields),
      });

      const body = await response.json().catch(() => null);
      const detail = readableMessage(body);

      if (!response.ok) {
        // A 400 is the backend's validation handler, which sends one short
        // sentence per rejected field. Anything else is the server having a bad
        // day and gets the generic line.
        setState("error");
        setMessage(detail ?? FALLBACK_ERROR);
        return;
      }

      const success = Boolean(body && (body as { success?: unknown }).success);
      setState(success ? "sent" : "error");
      setMessage(detail ?? (success ? "Thanks — that's with us." : FALLBACK_ERROR));
      if (success) setFields({ ...EMPTY, service: fields.service });
    } catch (error) {
      console.error("Enquiry failed:", error);
      setState("error");
      setMessage(
        "That did not go through — check your connection, or email neoncrowgames@gmail.com."
      );
    }
  };

  if (state === "sent") {
    return (
      <div className="enquiry enquiry--done" role="status">
        <h3 className="enquiry__done-title">Message sent</h3>
        <p className="prose">{message}</p>
        <button
          type="button"
          className="neon-btn neon-btn--sm neon-btn--ghost"
          onClick={() => {
            setState("idle");
            setMessage("");
          }}
        >
          Send another
        </button>
      </div>
    );
  }

  return (
    <form className="enquiry" onSubmit={onSubmit} noValidate={false}>
      <div className="enquiry__row">
        <div className="field">
          <label className="field__label" htmlFor={`${id}-name`}>
            Your name
          </label>
          <input
            id={`${id}-name`}
            className="neon-input"
            value={fields.name}
            onChange={set("name")}
            maxLength={120}
            autoComplete="name"
            required
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor={`${id}-email`}>
            Email
          </label>
          <input
            id={`${id}-email`}
            className="neon-input"
            type="email"
            value={fields.email}
            onChange={set("email")}
            maxLength={254}
            autoComplete="email"
            inputMode="email"
            required
          />
        </div>
      </div>

      <div className="enquiry__row">
        <div className="field">
          <label className="field__label" htmlFor={`${id}-org`}>
            Studio or publisher <span className="field__optional">optional</span>
          </label>
          <input
            id={`${id}-org`}
            className="neon-input"
            value={fields.organisation}
            onChange={set("organisation")}
            maxLength={160}
            autoComplete="organization"
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor={`${id}-stage`}>
            Where is the game? <span className="field__optional">optional</span>
          </label>
          <select
            id={`${id}-stage`}
            className="neon-input"
            value={fields.stage}
            onChange={set("stage")}
          >
            <option value="">Prefer not to say</option>
            {STAGES.map((stage) => (
              <option key={stage.value} value={stage.value}>
                {stage.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="field">
        <label className="field__label" htmlFor={`${id}-service`}>
          What are you asking about?
        </label>
        <select
          id={`${id}-service`}
          className="neon-input"
          value={fields.service}
          onChange={set("service")}
          required
        >
          {SERVICES.map((service) => (
            <option key={service.slug} value={service.slug}>
              {service.name}
            </option>
          ))}
          <option value="something-else">Something else</option>
        </select>
      </div>

      <div className="field">
        <label className="field__label" htmlFor={`${id}-message`}>
          Tell us about the game
        </label>
        <textarea
          id={`${id}-message`}
          className="neon-input enquiry__message"
          value={fields.message}
          onChange={set("message")}
          rows={6}
          minLength={20}
          maxLength={8000}
          placeholder="What is it, who plays it, and what would you like it to become?"
          required
        />
        <p className="field__hint">
          {fields.message.trim().length < 20
            ? "A sentence or two at least."
            : `${fields.message.trim().length} characters`}
        </p>
      </div>

      {/* Honeypot. Hidden from people and from screen readers, left in the DOM
          for anything filling in every field it can find. Not `display:none`,
          which some bots check for — off-canvas, and skipped by tab order. */}
      <div className="hp" aria-hidden="true">
        <label htmlFor={`${id}-website`}>Website</label>
        <input
          id={`${id}-website`}
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={fields.website}
          onChange={set("website")}
        />
      </div>

      {message && (
        <p className={`enquiry__message-line${state === "error" ? " is-error" : ""}`} role="status">
          {message}
        </p>
      )}

      <button className="neon-btn" type="submit" disabled={state === "sending"}>
        {state === "sending" ? "SENDING..." : "SEND ENQUIRY"}
      </button>

      <p className="field__hint">
        We read every one of these. Expect a reply within a couple of days — and an
        honest answer if it is not something we should take on.
      </p>
    </form>
  );
}
